import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formTemplateApi } from '../../services/formTemplates';
import { syncService } from '../../services/SyncService';
import TemplateCard from './TemplateCard';
import TemplateVersionDialog from './TemplateVersionDialog';
import DuplicateTemplateDialog from './DuplicateTemplateDialog';
import TemplatePreviewDialog from './TemplatePreviewDialog';

export default function TemplateLibrary({ token, userId, activeMode, isClinicAdmin, administeredClinics = [], getRoleForClinic, onEdit }) {
  const [templates, setTemplates] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newSpec, setNewSpec] = useState('');
  const [newScope, setNewScope] = useState(''); // For Everything mode scope selection

  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [selectedTemplateForVersions, setSelectedTemplateForVersions] = useState(null);

  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedTemplateForDuplicate, setSelectedTemplateForDuplicate] = useState(null);
  const [duplicateSourceVersionId, setDuplicateSourceVersionId] = useState(null);
  const [duplicateSourceVersionNumber, setDuplicateSourceVersionNumber] = useState(null);

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewVersionId, setPreviewVersionId] = useState(null);
  const [previewTemplateName, setPreviewTemplateName] = useState('');

  const [isCreating, setIsCreating] = useState(false);
  const [pendingActionId, setPendingActionId] = useState(null);

  const isClinicAdminForTemplate = family => {
    if (!family) return false;
    if (family.owner_type === 'user') return String(family.owner_id) === String(userId) || !userId || isClinicAdmin;
    if (getRoleForClinic) return getRoleForClinic(family.owner_id) === 'admin';
    if (administeredClinics.some(c => String(c.id) === String(family.owner_id))) return true;
    return isClinicAdmin;
  };

  const handleOpenPreview = (versionId, templateName) => {
    if (!versionId) return;
    setPreviewVersionId(versionId);
    setPreviewTemplateName(templateName);
    setPreviewDialogOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await formTemplateApi.list(token);
      setTemplates(all.filter(item => {
        if (activeMode === 'everything') return true;
        if (activeMode === 'personal') return item.owner_type === 'user' && item.owner_id === userId;
        return (item.owner_type === 'clinic' && item.owner_id === activeMode)
          || (item.owner_type === 'user' && item.owner_id === userId);
      }));
      setOffers(isClinicAdmin && !['personal', 'everything'].includes(activeMode)
        ? (await formTemplateApi.listOffers(activeMode, token)).filter(item => item.status === 'pending')
        : []);
    } catch (err) {
      const message = err.response?.data?.detail || 'Unable to load custom forms';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [activeMode, isClinicAdmin, token, userId]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = async family => {
    try {
      const draft = await formTemplateApi.createDraft(family.id, token);
      onEdit(draft, family);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open draft');
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || isCreating) return;
    let ownerType = 'user';
    let ownerId;
    if (activeMode === 'everything') {
      if (!newScope) return;
      if (newScope === 'personal') {
        ownerType = 'user';
      } else {
        ownerType = 'clinic';
        ownerId = newScope;
      }
    } else if (!['personal'].includes(activeMode) && isClinicAdmin) {
      ownerType = 'clinic';
      ownerId = activeMode;
    }
    setIsCreating(true);
    try {
      const family = await formTemplateApi.create({
        name: newName.trim(),
        description: newDesc.trim(),
        specialty: newSpec.trim(),
        owner_type: ownerType,
        ...(ownerId ? { owner_id: ownerId } : {}),
      }, token);
      setCreateOpen(false);
      setNewName('');
      setNewDesc('');
      setNewSpec('');
      setNewScope('');
      onEdit(family.draft, family);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create template');
    } finally {
      setIsCreating(false);
    }
  };

  const handleAdopt = async offer => {
    if (pendingActionId) return;
    setPendingActionId(`adopt-${offer.id}`);
    try {
      await formTemplateApi.adopt(offer.id, token);
      toast.success('Template added to clinic library');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add template to clinic library');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleDecline = async offer => {
    if (pendingActionId) return;
    setPendingActionId(`decline-${offer.id}`);
    try {
      await formTemplateApi.decline(offer.id, token);
      toast.success('Offer declined');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline offer');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleShareWithClinic = async (template) => {
    if (pendingActionId) return;
    setPendingActionId(`share-${template.id}`);
    try {
      await formTemplateApi.offer(template.active_version_id, activeMode, token);
      toast.success('Template shared with clinic administrators');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to share template');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleShareVersion = async (versionId) => {
    if (pendingActionId) return;
    setPendingActionId(`share-${versionId}`);
    try {
      await formTemplateApi.offer(versionId, activeMode, token);
      toast.success('Version shared with clinic administrators');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to share version');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleArchive = async family => {
    if (pendingActionId) return;
    if (!window.confirm(`Archive ${family.name}? Existing assessments remain available.`)) return;
    setPendingActionId(`archive-${family.id}`);
    try {
      await formTemplateApi.archive(family.id, token);
      toast.success('Template archived');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive template');
    } finally {
      setPendingActionId(null);
    }
  };

  const handleSelectVersion = async (templateId, versionId) => {
    try {
      await formTemplateApi.activate(templateId, versionId, token);
      toast.success('Version selected for new assessments');
      setVersionDialogOpen(false);
      if (!syncService.token && token) syncService.token = token;
      await syncService.pullData().catch(err => console.error('Sync error:', err));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to select version');
    }
  };

  const handleDuplicateFromVersion = (versionId, versionNumber) => {
    setDuplicateSourceVersionId(versionId);
    setDuplicateSourceVersionNumber(versionNumber);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateSubmit = async (versionId, payload) => {
    try {
      const forked = await formTemplateApi.fork(versionId, payload, token);
      toast.success(`Template duplicated as ${forked.name}`);
      await load();
      onEdit(forked.draft, forked);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate template');
    }
  };

  const personalTemplates = templates.filter(t => t.owner_type === 'user');
  const clinicTemplates = templates.filter(t => t.owner_type === 'clinic');

  const needsScopeSelection = activeMode === 'everything';
  const createDisabled = !newName.trim() || (needsScopeSelection && !newScope) || isCreating;

  return (
    <section aria-labelledby="custom-forms-title" className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 id="custom-forms-title" className="text-2xl font-bold">Custom Forms</h1>
          <p className="text-sm text-slate-500">Create private forms or manage clinic-owned libraries.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>New template</Button>
      </header>

      {offers.map(offer => (
        <article key={offer.id} className="flex items-center justify-between p-4 border rounded-lg bg-blue-50 border-blue-200">
          <div>
            <h2 className="font-semibold text-blue-900">{offer.source_template_name}</h2>
            <p className="text-xs text-blue-700">Offered to this clinic</p>
          </div>
          <div className="flex gap-2">
            <Button aria-label={`Add ${offer.source_template_name} to clinic library`} onClick={() => handleAdopt(offer)}>
              Add to clinic library
            </Button>
            <Button aria-label={`Decline ${offer.source_template_name}`} variant="ghost" onClick={() => handleDecline(offer)}>Decline</Button>
          </div>
        </article>
      ))}

      {loading ? (
        <p className="text-slate-500 py-4">Loading forms…</p>
      ) : error ? (
        <p className="text-red-600 py-4">{error}</p>
      ) : templates.length === 0 ? (
        <div className="p-8 text-center border border-dashed rounded-lg bg-slate-50">
          <p className="text-slate-600 font-medium">No custom form templates available.</p>
          <p className="text-sm text-slate-400 mt-1">Click &quot;New template&quot; to create your first assessment form.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {personalTemplates.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-slate-800 border-b pb-2">Personal Templates</h2>
              <div className="grid gap-4">
                {personalTemplates.map(family => (
                  <TemplateCard
                    key={family.id}
                    template={family}
                    userId={userId}
                    activeMode={activeMode}
                    isClinicAdmin={isClinicAdminForTemplate(family)}
                    onEdit={handleEdit}
                    onOpenVersions={t => { setSelectedTemplateForVersions(t); setVersionDialogOpen(true); }}
                    onPreview={f => handleOpenPreview(f.active_version_id, f.name)}
                    onOffer={handleShareWithClinic}
                    onArchive={handleArchive}
                  />
                ))}
              </div>
            </div>
          )}

          {clinicTemplates.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-slate-800 border-b pb-2">Clinic Library</h2>
              <div className="grid gap-4">
                {clinicTemplates.map(family => (
                  <TemplateCard
                    key={family.id}
                    template={family}
                    userId={userId}
                    activeMode={activeMode}
                    isClinicAdmin={isClinicAdminForTemplate(family)}
                    onEdit={handleEdit}
                    onOpenVersions={t => { setSelectedTemplateForVersions(t); setVersionDialogOpen(true); }}
                    onPreview={f => handleOpenPreview(f.active_version_id, f.name)}
                    onOffer={handleShareWithClinic}
                    onArchive={handleArchive}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Template Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New template</DialogTitle>
            <DialogDescription>
              Create a new assessment form template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-template-name">Template name</Label>
              <Input id="new-template-name" value={newName} onChange={event => setNewName(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-template-desc">Description</Label>
              <Input id="new-template-desc" value={newDesc} onChange={event => setNewDesc(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-template-spec">Specialty</Label>
              <Input id="new-template-spec" value={newSpec} onChange={event => setNewSpec(event.target.value)} />
            </div>
            {needsScopeSelection && (
              <div className="space-y-2">
                <Label htmlFor="new-template-scope">Owner scope</Label>
                <select
                  id="new-template-scope"
                  className="w-full p-2 border rounded"
                  value={newScope}
                  aria-label="Owner scope"
                  onChange={e => setNewScope(e.target.value)}
                >
                  <option value="">Select scope…</option>
                  <option value="personal">Personal</option>
                  {administeredClinics.map(clinic => (
                    <option key={clinic.id} value={clinic.id}>{clinic.name || clinic.id}</option>
                  ))}
                </select>
              </div>
            )}
            <Button disabled={createDisabled} onClick={handleCreate}>Create template</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <TemplateVersionDialog
        open={versionDialogOpen}
        onOpenChange={setVersionDialogOpen}
        template={selectedTemplateForVersions}
        token={token}
        isOwner={
          selectedTemplateForVersions
            ? selectedTemplateForVersions.owner_type === 'user'
              ? !userId || String(selectedTemplateForVersions.owner_id) === String(userId)
              : isClinicAdminForTemplate(selectedTemplateForVersions)
            : false
        }
        onSelectVersion={handleSelectVersion}
        onDuplicate={
          selectedTemplateForVersions && isClinicAdminForTemplate(selectedTemplateForVersions)
            ? (versionId, versionNumber) => {
                setSelectedTemplateForDuplicate(selectedTemplateForVersions);
                handleDuplicateFromVersion(versionId, versionNumber);
              }
            : undefined
        }
        onPreview={vId => handleOpenPreview(vId, selectedTemplateForVersions?.name)}
        onShare={!['personal', 'everything'].includes(activeMode) && selectedTemplateForVersions?.owner_type === 'user'
          ? handleShareVersion
          : undefined
        }
      />

      {/* Duplicate Dialog */}
      <DuplicateTemplateDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
        template={selectedTemplateForDuplicate}
        sourceVersionId={duplicateSourceVersionId}
        sourceVersionNumber={duplicateSourceVersionNumber}
        isClinicAdmin={isClinicAdmin}
        activeMode={activeMode}
        onDuplicateSubmit={handleDuplicateSubmit}
      />

      {/* Preview Dialog */}
      <TemplatePreviewDialog
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
        versionId={previewVersionId}
        templateName={previewTemplateName}
        token={token}
      />
    </section>
  );
}

TemplateLibrary.propTypes = {
  token: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  activeMode: PropTypes.string.isRequired,
  isClinicAdmin: PropTypes.bool.isRequired,
  administeredClinics: PropTypes.array,
  getRoleForClinic: PropTypes.func,
  onEdit: PropTypes.func.isRequired,
};
